import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { 
  useBrainstormingItems, 
  useCreateBrainstormingItem, 
  useUpdateBrainstormingItem, 
  useDeleteBrainstormingItem,
  useDevelopIdea,
  useAnalyzeIdeaWithAI,
  useUpdateBrainstormingItemAIAnalysis,
  useTenantInfo,
  useProjects,
  useUsers
} from '../hooks/useQueries';
import { useHasRole } from '../hooks/useHasRole';
import Toast from '../components/Popup/Toast';
import Modal from '../components/Popup/Modal';
import DevelopIdeaForm from '../components/DevelopIdeaForm';
import SearchFilters from '../components/Search/SearchFilters';
import { formatItemRef } from '../utils/itemIdFormat';
import './brainstorming.css';

// Hidden on this instance: the RIIMS Bot AI analysis UI in the Develop Idea modal.
const SHOW_AI_ASSISTANT = false;

export default function Brainstorming() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showNewIdeaModal, setShowNewIdeaModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, cardId: null, title: '' });
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newIsBusinessDevelopment, setNewIsBusinessDevelopment] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isDevelopModalOpen, setIsDevelopModalOpen] = useState(false);
  const [selectedItemForDevelopment, setSelectedItemForDevelopment] = useState(null);
  const [showDeveloped, setShowDeveloped] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [expandedCards, setExpandedCards] = useState({}); // Track which cards are expanded
  const [isAnalyzingInModal, setIsAnalyzingInModal] = useState(false); // Track modal AI analysis
  const [modalAiAnalysis, setModalAiAnalysis] = useState(null); // Store modal AI analysis result
  const [developmentSuccessPopup, setDevelopmentSuccessPopup] = useState({ show: false, itemId: null }); // Development success popup
  const formRef = useRef();

  const tryParseJson = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  };

  const getAnalysisDisplayText = (analysisValue) => {
    if (analysisValue == null) return '';
    if (typeof analysisValue !== 'string') return String(analysisValue);

    const parsed = tryParseJson(analysisValue);
    if (parsed && typeof parsed.analysis === 'string') return parsed.analysis;
    return analysisValue;
  };

  // React Query hooks
  const { data: brainstormingItems = [], isLoading: loading, error } = useBrainstormingItems();
  const { data: tenantInfo, isLoading: tenantLoading } = useTenantInfo();
  const { data: projects = [] } = useProjects();
  const { hasRole: isAdmin } = useHasRole('admin');
  // Viewers read the board but cannot create, edit, archive, restore, delete or
  // develop cards. The server enforces this too - see requireUser() on the
  // brainstorming write routes.
  const { hasRole: canEditIdeas } = useHasRole('user');
  const { data: allUsers = [] } = useUsers();

  // created_by holds an email; show the person's name when we can resolve it.
  const formatCardCreator = (createdBy) => {
    if (!createdBy) return 'Unknown';
    const match = allUsers.find(
      (user) => (user.email || '').toLowerCase() === String(createdBy).toLowerCase()
    );
    return match?.name ? `${match.name} (${createdBy})` : String(createdBy);
  };
  const createMutation = useCreateBrainstormingItem();
  const updateMutation = useUpdateBrainstormingItem();
  const deleteMutation = useDeleteBrainstormingItem();
  const developMutation = useDevelopIdea();
  const analyzeIdeaMutation = useAnalyzeIdeaWithAI();
  const updateAiAnalysisMutation = useUpdateBrainstormingItemAIAnalysis();

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const handleOpenDevelopModal = (item) => {
    setSelectedItemForDevelopment(item);
    setIsDevelopModalOpen(true);
    // Check if item already has AI analysis, if so, load it
    if (item.ai_analysis) {
      // Some environments store ai_analysis as a JSON string; normalize to object when possible
      if (typeof item.ai_analysis === 'string') {
        setModalAiAnalysis(tryParseJson(item.ai_analysis) || item.ai_analysis);
      } else {
        setModalAiAnalysis(item.ai_analysis);
      }
    } else {
      setModalAiAnalysis(null);
    }
    setIsAnalyzingInModal(false);
  };

  const handleCloseDevelopModal = () => {
    setIsDevelopModalOpen(false);
    setSelectedItemForDevelopment(null);
  };

  const handleOpenArchiveModal = (item) => {
    setSelectedItemForDevelopment(item);
    setIsArchiveModalOpen(true);
    setArchiveReason('');
  };

  const handleCloseArchiveModal = () => {
    setIsArchiveModalOpen(false);
    setArchiveReason('');
  };

  const handleConfirmArchive = () => {
    if (selectedItemForDevelopment) {
      developMutation.mutate({
        brainstormingId: selectedItemForDevelopment.id,
        title: selectedItemForDevelopment.description.substring(0, 50),
        categoryId: null,
        subcategoryId: null,
        description: selectedItemForDevelopment.description,
        initialNotes: '',
        developColumn: 'Archived',
        abandonReason: archiveReason.trim()
      });
      handleCloseArchiveModal();
      setSelectedItemForDevelopment(null);
    }
  };

  const handleAnalyzeIdeaInModal = async () => {
    if (!selectedItemForDevelopment) return;
    
    try {
      setIsAnalyzingInModal(true);
      
      // Get current values from the form
      let currentTitle, currentDescription;
      if (formRef.current && formRef.current.getCurrentValues) {
        const currentValues = formRef.current.getCurrentValues();
        currentTitle = currentValues.title || selectedItemForDevelopment.title || selectedItemForDevelopment.description.substring(0, 50);
        currentDescription = currentValues.description || selectedItemForDevelopment.description;
      } else {
        // Fallback to stored values if form ref is not available
        currentTitle = selectedItemForDevelopment.title || selectedItemForDevelopment.description.substring(0, 50);
        currentDescription = selectedItemForDevelopment.description;
      }
      
      const result = await analyzeIdeaMutation.mutateAsync({
        title: currentTitle,
        description: currentDescription
      });
      
      // Handle uncertain AI responses
      if (result.uncertain) {
        showToast('AI could not confidently categorize this idea. Please provide more specific details or categorize manually.', 'warning');
        setModalAiAnalysis(null);
        return;
      }
      
      const analysisData = result.analysis;
      setModalAiAnalysis(analysisData);
      
      // Save the AI analysis to the database
      try {
        await updateAiAnalysisMutation.mutateAsync({
          id: selectedItemForDevelopment.id,
          aiAnalysis: analysisData
        });
        console.log('AI analysis saved to database');
      } catch (saveError) {
        console.error('Error saving AI analysis:', saveError);
        // Continue even if saving fails - user still gets the analysis
      }
      
      showToast('AI analysis completed!', 'success');
    } catch (error) {
      showToast('Failed to analyze idea with AI', 'error');
      console.error('AI Analysis error:', error);
    } finally {
      setIsAnalyzingInModal(false);
    }
  };

  const handleClearAnalysis = async () => {
    if (!selectedItemForDevelopment) return;
    
    try {
      // Clear analysis from database
      await updateAiAnalysisMutation.mutateAsync({
        id: selectedItemForDevelopment.id,
        aiAnalysis: null
      });
      
      // Clear from modal state
      setModalAiAnalysis(null);
      showToast('AI analysis cleared!', 'success');
    } catch (error) {
      showToast('Failed to clear AI analysis', 'error');
      console.error('Clear analysis error:', error);
    }
  };

  const toggleCardExpansion = (itemId) => {
    setExpandedCards(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleSaveDevelopment = async (formData) => {
    try {
      // Add developColumn for regular development and map projectId to project_id for backend
      const dataWithColumn = {
        ...formData,
        developColumn: 'Developed',
        project_id: formData.projectId  // Map projectId to project_id for backend compatibility
      };
      const result = await developMutation.mutateAsync(dataWithColumn);
      handleCloseDevelopModal();
      
      // Show the development success popup instead of toast
      if (result && result.newItem && result.newItem.id) {
        setDevelopmentSuccessPopup({ show: true, itemId: result.newItem.id });
        
        // Auto-hide after 4 seconds
        setTimeout(() => {
          setDevelopmentSuccessPopup({ show: false, itemId: null });
        }, 4000);
      } else {
        showToast('Idea development saved successfully!');
      }
    } catch (error) {
      showToast('Failed to develop idea', 'error');
    }
  };

  const handleModalConfirm = () => {
    if (formRef.current) {
      return formRef.current.submit();
    }
    return false;
  };

  const checkFormValidity = () => {
    if (formRef.current) {
      setIsFormValid(formRef.current.isValid());
    }
  };

  // Check form validity periodically
  React.useEffect(() => {
    if (isDevelopModalOpen) {
      const interval = setInterval(checkFormValidity, 500);
      return () => clearInterval(interval);
    }
  }, [isDevelopModalOpen]);

  const handleCreateCard = async () => {
    if (!newTitle.trim()) {
      showToast('Please enter a title', 'error');
      return false;
    }
    if (!newDescription.trim()) {
      showToast('Please enter a description', 'error');
      return false;
    }

    try {
      await createMutation.mutateAsync({
        title: newTitle.trim(),
        description: newDescription.trim(),
        is_business_development: newIsBusinessDevelopment
      });
      setNewTitle('');
      setNewDescription('');
      setNewIsBusinessDevelopment(false);
      setShowNewIdeaModal(false);
      showToast('Brainstorming card created successfully');
      return true;
    } catch (error) {
      showToast('Failed to create brainstorming card', 'error');
      return false;
    }
  };

  const handleCloseNewIdeaModal = () => {
    setShowNewIdeaModal(false);
    setNewTitle('');
    setNewDescription('');
    setNewIsBusinessDevelopment(false);
  };

  const handleUpdateCard = async (id, description, title) => {
    // Find the item to check if it's developed
    const item = brainstormingItems.find(item => item.id === id);
    if (item && item.developed === 'Developed') {
      showToast('Cannot edit developed ideas', 'error');
      return;
    }
    
    if (!description.trim()) {
      showToast('Please enter a description', 'error');
      return;
    }
    // Allow empty title - it will be set to null on the server
    // if (title !== undefined && !title.trim()) {
    //   showToast('Please enter a title', 'error');
    //   return;
    // }

    try {
      // If title is provided, update both title and description
      if (title !== undefined) {
        console.log('Updating card with title:', { id, description: description.trim(), title: title.trim() });
        await updateMutation.mutateAsync({ id, description: description.trim(), title: title.trim() });
      } else {
        console.log('Updating card without title:', { id, description: description.trim() });
        await updateMutation.mutateAsync({ id, description: description.trim() });
      }
      showToast('Brainstorming card updated successfully');
    } catch (error) {
      showToast('Failed to update brainstorming card', 'error');
    }
  };

  const handleDeleteCard = (id) => {
    const card = brainstormingItems.find(item => item.id === id);
    setDeleteModal({ 
      show: true, 
      cardId: id, 
      title: card?.title || 'Unknown Card'
    });
  };

  const confirmDeleteCard = async () => {
    try {
      await deleteMutation.mutateAsync(deleteModal.cardId);
      showToast('Brainstorming card moved to trash');
      setDeleteModal({ show: false, cardId: null, title: '' });
    } catch (error) {
      showToast('Failed to delete brainstorming card', 'error');
    }
  };

  const handleRestoreCard = async (id) => {
    // Find the item to get its current description
    const item = brainstormingItems.find(item => item.id === id);
    if (!item) {
      showToast('Item not found', 'error');
      return;
    }

    try {
      await updateMutation.mutateAsync({ 
        id, 
        description: item.description,
        developed: null 
      });
      showToast('Idea restored to active ideas');
    } catch (error) {
      showToast('Failed to restore idea', 'error');
    }
  };

  // Show query errors as a side effect to avoid render loops.
  useEffect(() => {
    if (!error) return;

    const message = error?.message
      ? `Failed to fetch brainstorming items: ${error.message}`
      : 'Failed to fetch brainstorming items';
    showToast(message, 'error');
  }, [error]);

  // Filter items by search term
  const filteredItems = brainstormingItems.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Categorize brainstorming items
  const undevelopedItems = filteredItems.filter(item => !item.developed || item.developed === 'Not Started');
  const developedItems = filteredItems.filter(item => item.developed === 'Developed');
  const archivedItems = filteredItems.filter(item => item.developed === 'Archived');

  const BrainstormingCard = ({ item }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(item.title);
    const [editDescription, setEditDescription] = useState(item.description);
    const archiveReasonText = (
      item?.abandoned_reason ||
      item?.abandonedReason ||
      item?.abandon_reason ||
      item?.abandonReason ||
      ''
    ).trim();

    const handleEdit = () => {
      setIsEditing(true);
      setEditTitle(item.title);
      setEditDescription(item.description);
    };

    const handleSave = () => {
      handleUpdateCard(item.id, editDescription, editTitle);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setIsEditing(false);
      setEditTitle(item.title);
      setEditDescription(item.description);
    };

    return (
              <div key={item.id} className={`brainstorming-card ${expandedCards[item.id] ? 'expanded' : ''}`}>
        <div className="card-header">
          {isEditing ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="edit-title-input"
              style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2c3e50', flex: 1, marginRight: '8px', borderRadius: '4px', border: '1px solid #d1d5db', padding: '4px 8px' }}
              placeholder="Enter title..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSave();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
            />
          ) : (
            <span className="card-title" style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2c3e50', flex: 1 }}>
              {item.title || '(No Title)'}
            </span>
          )}
          <div className="card-actions">
            {!isEditing && canEditIdeas && (
              <>
                {item.developed === 'Archived' ? (
                  <button 
                    className="btn-restore"
                    onClick={() => handleRestoreCard(item.id)}
                    title="Restore to Active Ideas"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                ) : item.developed !== 'Developed' ? (
                  <>
                    <button 
                      className="btn-edit"
                      onClick={handleEdit}
                      title="Edit"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button 
                      className="btn-archive"
                      onClick={() => handleOpenArchiveModal(item)}
                      title="Archive Idea"
                    >
                      <i className="bi bi-archive"></i>
                    </button>
                  </>
                ) : null}
                <button 
                  className="btn-delete"
                  onClick={() => handleDeleteCard(item.id)}
                  title="Delete"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className="card-content">
          {isEditing ? (
            <div className="edit-form">
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="edit-textarea"
                style={{ marginTop: '8px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    handleSave();
                  } else if (e.key === 'Escape') {
                    handleCancel();
                  }
                }}
              />
              <div className="edit-actions">
                <button 
                  className="btn-save"
                  onClick={handleSave}
                >
                  Save
                </button>
                <button 
                  className="btn-cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="card-description-container">
              <p className={`card-description ${!expandedCards[item.id] ? 'card-description-truncated' : ''}`}>
                {item.description}
              </p>
              {item.description && item.description.length > 150 && (
                <div className="expand-button-container">
                  <button 
                    className="expand-button"
                    onClick={() => toggleCardExpansion(item.id)}
                  >
                    <i className={`bi bi-chevron-${expandedCards[item.id] ? 'up' : 'down'} expand-chevron`}></i>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card-footer" style={{ marginTop: '16px' }}>
          {canEditIdeas && (!item.developed || item.developed === 'Not Started') && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button 
                className="btn-primary" 
                style={{ 
                  width: '100%',
                  backgroundColor: 'var(--nsi-brand)',
                  borderColor: 'var(--nsi-brand)',
                  color: 'white'
                }}
                onClick={() => handleOpenDevelopModal(item)}
              >
                Develop
              </button>
            </div>
          )}

          {/* Status indicators */}
          {item.developed === 'Developed' && (
            <div style={{ padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px', color: '#155724' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>✓ Developed</span>
                {item.developed_into_item_id && (
                  <button
                    onClick={() => navigate(`/items/${item.developed_into_item_id}`)}
                    style={{
                      background: 'none',
                      border: '1px solid #155724',
                      color: '#155724',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#c3e6cb'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    title={`View Item ${formatItemRef(item.developed_into_item_id)}`}
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                    View Item
                  </button>
                )}
              </div>
            </div>
          )}
          {item.developed === 'Archived' && (
            <div style={{ 
              padding: '8px', 
              backgroundColor: '#e3e6ea', 
              borderRadius: '4px', 
              color: '#495057',
              border: '1px solid #ced4da'
            }}>
              <i className="bi bi-archive" style={{ marginRight: '6px' }}></i>
              Archived
              {archiveReasonText && (
                <div style={{ marginTop: '6px', fontSize: '0.9rem', lineHeight: 1.3 }}>
                  <strong>Reason:</strong> {archiveReasonText}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="brainstorming-page">
      {/* Development Success Popup */}
      <div 
        className={`development-success-popup ${developmentSuccessPopup.show ? 'show' : ''}`}
        onClick={() => {
          if (developmentSuccessPopup.itemId) {
            navigate(`/items/${developmentSuccessPopup.itemId}`);
            setDevelopmentSuccessPopup({ show: false, itemId: null });
          }
        }}
      >
        <div className="popup-content">
          <div className="popup-icon">
            <i className="bi bi-check-circle-fill"></i>
          </div>
          <div className="popup-text">
            <span className="popup-title">Item {developmentSuccessPopup.itemId} successfully created</span>
            <span className="popup-subtitle">Click here to view</span>
          </div>
          <button 
            className="popup-close"
            onClick={(e) => {
              e.stopPropagation();
              setDevelopmentSuccessPopup({ show: false, itemId: null });
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      </div>

      <div className="brainstorming-container">
        {!isMobile && (
          <div className="page-header">
            <h1>Brainstorming</h1>
            {canEditIdeas && (
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  backgroundColor: 'var(--nsi-brand)',
                  borderColor: 'var(--nsi-brand)',
                  color: 'white'
                }}
                onClick={() => setShowNewIdeaModal(true)}
                aria-label="New Idea"
                title="New Idea"
              >
                <i className="bi bi-plus-circle"></i>
                <span className="new-idea-label">New Idea</span>
              </button>
            )}
          </div>
        )}

        {/* Search Filters */}
        <div className="brainstorming-toolbar">
          <SearchFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder={isMobile ? 'Search...' : 'Search ideas by title or description...'}
          >
            {isMobile && canEditIdeas && (
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  backgroundColor: 'var(--nsi-brand)',
                  borderColor: 'var(--nsi-brand)',
                  color: 'white'
                }}
                onClick={() => setShowNewIdeaModal(true)}
                aria-label="New Idea"
                title="New Idea"
              >
                <i className="bi bi-plus-circle"></i>
                <span className="new-idea-label">New Idea</span>
              </button>
            )}
          </SearchFilters>
        </div>



          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading brainstorming cards...</p>
            </div>
          ) : (
            <>
              {/* Undeveloped Items Section */}
              <div className="brainstorming-section">
                <h2>Active Ideas</h2>
                <div className="brainstorming-grid">
                  {undevelopedItems.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <i className="bi bi-lightbulb"></i>
                      </div>
                      <h3>No active ideas yet</h3>
                      {canEditIdeas ? (
                        <>
                          <p>Start by creating your first brainstorming card!</p>
                          <button
                            className="btn-primary create-first-idea-btn"
                            style={{
                              backgroundColor: 'var(--nsi-brand)',
                              borderColor: 'var(--nsi-brand)',
                              color: 'white',
                              marginTop: '5px'
                            }}
                            onClick={() => setShowNewIdeaModal(true)}
                          >
                            <i className="bi bi-plus-circle" style={{fontSize: '0.9rem', marginTop: '9px', color:'#fff'}}></i>
                            Create First Idea
                          </button>
                        </>
                      ) : (
                        <p>Ideas added by your team will appear here.</p>
                      )}
                    </div>
                  ) : (
                    undevelopedItems.map((item) => (
                      <BrainstormingCard key={item.id} item={item} />
                    ))
                  )}
                </div>
              </div>

              {/* Developed Items Accordion */}
              {developedItems.length > 0 && (
                <div className="brainstorming-section">
                  <div className="accordion-header" onClick={() => setShowDeveloped(!showDeveloped)}>
                    <h3>Developed Ideas ({developedItems.length})</h3>
                    <i className={`bi ${showDeveloped ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </div>
                  <div className={`accordion-content ${showDeveloped ? 'expanded' : 'collapsed'}`}>
                    <div className="brainstorming-grid">
                      {developedItems.map((item) => (
                        <BrainstormingCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Archived Items Accordion */}
              {archivedItems.length > 0 && (
                <div className="brainstorming-section">
                  <div className="accordion-header" onClick={() => setShowArchived(!showArchived)}>
                    <h3>Archived Ideas ({archivedItems.length})</h3>
                    <i className={`bi ${showArchived ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </div>
                  <div className={`accordion-content ${showArchived ? 'expanded' : 'collapsed'}`}>
                    <div className="brainstorming-grid">
                      {archivedItems.map((item) => (
                        <BrainstormingCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <Modal
            show={isDevelopModalOpen}
            onCancel={handleCloseDevelopModal}
            onConfirm={handleModalConfirm}
            confirmText="Develop"
            cancelText="Cancel"
            confirmDisabled={!isFormValid}
            loading={developMutation.isPending || isAnalyzingInModal}
            title="Develop Idea"
            size={isMobile ? 'modal-fullscreen' : 'modal-dialog-centered'}
            headerExtra={SHOW_AI_ASSISTANT && !modalAiAnalysis ? (
              <div className="tooltip-container" style={{ position: 'relative' }}>
                <button
                  onClick={handleAnalyzeIdeaInModal}
                  disabled={isAnalyzingInModal || (tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota)}
                  style={{
                    backgroundColor: tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota 
                      ? '#dc3545' 
                      : 'var(--nsi-brand)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota ? 0.7 : 1
                  }}
                >
                  {isAnalyzingInModal ? (
                    <>
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Analyzing...
                    </>
                  ) : tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota ? (
                    <>
                      <i className="bi bi-exclamation-triangle"></i>
                      Daily limit reached
                    </>
                  ) : (
                    <>
                      <i className="bi bi-robot"></i>
                      RIIMS Bot AI
                    </>
                  )}
                </button>
                <div className="css-tooltip css-tooltip-bottom" style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '8px',
                  zIndex: 10001,
                  minWidth: '200px',
                  whiteSpace: 'normal'
                }}>
                  {tenantInfo && !tenantLoading ? (
                    `${tenantInfo.ai_request_count} of ${tenantInfo.ai_request_quota} daily requests used`
                  ) : (
                    'Loading request information...'
                  )}
                </div>
              </div>
            ) : null}
            content={selectedItemForDevelopment && (
              <div>
                {/* AI Analysis Results */}
                {SHOW_AI_ASSISTANT && modalAiAnalysis && modalAiAnalysis.categoryId && modalAiAnalysis.subcategoryId && (
                  <div style={{ 
                    marginBottom: '20px',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <i className="bi bi-robot" style={{ color: 'var(--nsi-brand)', fontSize: '16px' }}></i>
                        <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>
                          AI Analysis
                        </span>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: modalAiAnalysis.classification === 'RISK' ? '#fee2e2' : '#dcfce7',
                          color: modalAiAnalysis.classification === 'RISK' ? '#dc2626' : '#16a34a'
                        }}>
                          {modalAiAnalysis.classification}
                        </span>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: '#e0e7ff',
                          color: '#4338ca'
                        }}>
                          {modalAiAnalysis.categoryName} → {modalAiAnalysis.subcategoryName}
                        </span>
                      </div>
                      
                      {/* Regenerate Button */}
                      <div className="tooltip-container" style={{ position: 'relative' }}>
                        <button
                          onClick={handleAnalyzeIdeaInModal}
                          disabled={isAnalyzingInModal || (tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota)}
                          style={{
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                              padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            opacity: tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota ? 0.7 : 1
                          }}
                        >
                          {isAnalyzingInModal ? (
                            <>
                              <div style={{ 
                                width: '10px', 
                                height: '10px', 
                                border: '2px solid #ffffff',
                                borderTop: '2px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></div>
                              Analyzing...
                            </>
                          ) : tenantInfo && tenantInfo.ai_request_count >= tenantInfo.ai_request_quota ? (
                            <>
                              <i className="bi bi-exclamation-triangle"></i>
                              Limit reached
                            </>
                          ) : (
                            <>
                              <i className="bi bi-arrow-clockwise"></i>
                              Regenerate
                            </>
                          )}
                        </button>
                        <div className="css-tooltip css-tooltip-bottom" style={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginTop: '8px',
                          zIndex: 10001,
                          minWidth: '200px',
                          whiteSpace: 'normal'
                        }}>
                          {tenantInfo && !tenantLoading ? (
                            `${tenantInfo.ai_request_count} of ${tenantInfo.ai_request_quota} daily requests used`
                          ) : (
                            'Loading request information...'
                          )}
                        </div>

                        {/* Clear Analysis Button - Icon Only */}
                        <button
                          onClick={handleClearAnalysis}
                          disabled={isAnalyzingInModal}
                          title="Clear AI analysis"
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: isAnalyzingInModal ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isAnalyzingInModal ? 0.7 : 1,
                            minWidth: '32px',
                            marginLeft: '8px',
                            verticalAlign: 'top'
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>

                    </div>
                    <p style={{ 
                      margin: '0 0 12px 0',
                      fontSize: '14px',
                      color: '#374151',
                      lineHeight: 1.5
                    }}>
                      {getAnalysisDisplayText(modalAiAnalysis.analysis)}
                    </p>
                    
                    {/* Similarity Recommendations */}
                    {modalAiAnalysis.similarItems && modalAiAnalysis.similarItems.length > 0 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '6px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#856404'
                        }}>
                          <i className="bi bi-exclamation-triangle"></i>
                          Similar Items Found
                        </div>
                        <p style={{ 
                          margin: '0 0 8px 0',
                          fontSize: '13px',
                          color: '#856404',
                          lineHeight: 1.4
                        }}>
                          {modalAiAnalysis.similarityRecommendation}
                        </p>
                        {modalAiAnalysis.similarItems.map((item, index) => (
                          <div key={index} style={{
                            padding: '8px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e9ecef',
                            borderRadius: '4px',
                            marginBottom: index < modalAiAnalysis.similarItems.length - 1 ? '6px' : '0'
                          }}>
                            <div style={{ 
                              fontWeight: '500', 
                              fontSize: '12px',
                              color: '#495057',
                              marginBottom: '4px'
                            }}>
                              Item {formatItemRef(item.itemId)}: {item.title}
                            </div>
                            <div style={{ 
                              fontSize: '11px',
                              color: '#6c757d',
                              fontStyle: 'italic'
                            }}>
                              {item.reason}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Develop Form. Cards analysed before the assistant was hidden
                    still carry a stored analysis, so gate what is passed in too —
                    otherwise its suggestion chips would resurface in the form. */}
                <DevelopIdeaForm
                  ref={formRef}
                  item={selectedItemForDevelopment}
                  onSave={handleSaveDevelopment}
                  onCancel={handleCloseDevelopModal}
                  disabled={isAnalyzingInModal}
                  aiAnalysis={SHOW_AI_ASSISTANT ? modalAiAnalysis : null}
                  projects={projects}
                />

                {isAdmin && (
                  <div className="card-creator-note">
                    <i className="bi bi-person-badge"></i>
                    <span>
                      Created by{' '}
                      <strong>{formatCardCreator(selectedItemForDevelopment.created_by)}</strong>
                    </span>
                    <span className="card-creator-note-admin">Admins only</span>
                  </div>
                )}
              </div>
            )}
            footer={
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary"
                  style={{ minHeight: 40, padding: '0.75rem 1.5rem', fontSize: '0.875rem', fontWeight: 500 }}
                  onClick={handleCloseDevelopModal}
                  disabled={developMutation.isPending || isAnalyzingInModal}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  style={{ 
                    minHeight: 40, 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem', 
                    fontWeight: 500,
                    backgroundColor: 'var(--nsi-brand)',
                    borderColor: 'var(--nsi-brand)',
                    color: 'white'
                  }}
                  onClick={handleModalConfirm}
                  disabled={!isFormValid || developMutation.isPending || isAnalyzingInModal}
                >
                  {developMutation.isPending ? 'Saving...' : 'Develop'}
                </button>
              </div>
            }
          />

          {/* Archive Reason Modal */}
          <Modal
            show={isArchiveModalOpen}
            onCancel={handleCloseArchiveModal}
            onConfirm={handleConfirmArchive}
            confirmText="Archive Idea"
            cancelText="Cancel"
            confirmBtnClass="btn-secondary"
            confirmDisabled={!archiveReason.trim()}
            loading={developMutation.isPending}
            title="Archive Idea"
            size={isMobile ? 'modal-fullscreen' : 'modal-dialog-centered'}
            content={
              <div style={{ padding: '1rem 0' }}>
                <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                  Please provide a reason for archiving this idea:
                </p>
                <textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="Enter reason for archiving this idea..."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  autoFocus
                />
              </div>
            }
          />

          {/* New Idea Modal */}
          <Modal
            show={showNewIdeaModal}
            onCancel={handleCloseNewIdeaModal}
            onConfirm={handleCreateCard}
            confirmText="Create Idea"
            cancelText="Cancel"
            confirmDisabled={!newTitle.trim() || !newDescription.trim()}
            loading={createMutation.isPending}
            title="Add New Brainstorming Idea"
            size={isMobile ? 'modal-fullscreen' : 'modal-dialog-centered'}
            content={
              <div style={{ padding: '1rem 0' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057' }}>
                    Title
                  </label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Enter a title..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                    autoFocus
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057' }}>
                    Description
                  </label>
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Enter your brainstorming idea..."
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            }
          />

          {/* Delete Confirmation Modal */}
          <Modal
            show={deleteModal.show}
            onCancel={() => setDeleteModal({ show: false, cardId: null, title: '' })}
            onConfirm={confirmDeleteCard}
            title="Delete Brainstorming Card"
            confirmText="Move to Trash"
            confirmBtnClass="btn-danger"
            loading={deleteMutation.isPending}
            size={isMobile ? 'modal-fullscreen' : 'modal-dialog-centered'}
            content={
              <div>
                <p>
                  Are you sure you want to delete this brainstorming card? It will be moved to the trashcan where it can be recovered for 30 days.
                </p>
                <div className="modal-item-preview">
                  <strong>{deleteModal.title}</strong>
                </div>
              </div>
            }
          />

          <Toast 
            show={toast.show}
            message={toast.message}
            type={toast.type}
            onClose={handleCloseToast}
          />
        </div>
      </div>
  );
}